import {
  invitationInfoSchema,
  type InvitationInfo,
  authSessionResponseSchema,
  forgotPasswordResponseSchema,
  loginResponseSchema,
  ssoStartResponseSchema,
  twoFactorActivationResponseSchema,
  twoFactorSetupSchema,
  type TwoFactorSetup,
  twoFactorSessionResponseSchema,
  type AuthSessionResponse,
  type AuthUser,
  type SessionPlatformName,
} from '@fluxus/shared';
import type { z } from 'zod';
import {
  apiDownload,
  apiFileOrQueued,
  apiRequest,
  type FileOrQueued,
  type RequestOptions,
} from '../api/client';
import { ApiError } from '../api/errors';
import type { Platform, SecureStorage } from '../platform';

export const REFRESH_TOKEN_KEY = 'auth.refresh-token';
/** Nome do lock (Web Locks) e do canal (BroadcastChannel) que coordenam as abas da web. */
export const AUTH_LOCK_NAME = 'fluxus-auth-refresh';
export const AUTH_CHANNEL_NAME = 'fluxus-auth';

/** Resultado do login: a sessão, ou a segunda etapa (verificação em duas etapas ativa). */
export type LoginOutcome =
  | { kind: 'session'; session: AuthSession }
  | {
      kind: 'two-factor';
      /** 'verify' = pedir o código; 'setup' = a empresa exige 2FA e é preciso ativar antes de entrar. */
      step: 'verify' | 'setup';
      challengeToken: string;
      expiresIn: number;
      /** Conta do desafio, quando a API informa (volta do SSO). */
      email?: string;
    };

/** Segunda etapa: código do app autenticador ou um código de recuperação. */
export type TwoFactorProof = { code: string } | { recoveryCode: string };

type PeerMessage = { type: 'session'; session: AuthSession } | { type: 'logout' };

const PLATFORM_NAME: Record<Platform, SessionPlatformName> = {
  web: 'WEB',
  desktop: 'DESKTOP',
  android: 'ANDROID',
  ios: 'IOS',
};

export type AuthSession = { accessToken: string; expiresAt: number; user: AuthUser };

/** Por que a sessão mudou: saída voluntária (aqui ou em outra aba) ou sessão que terminou/expirou. */
export type SessionChange = 'update' | 'logout' | 'ended';
type Listener = (session: AuthSession | null, change: SessionChange) => void;

export type AuthClientOptions = {
  platform: Platform;
  /** Armazenamento seguro dos apps (null na web, onde o refresh fica em cookie httpOnly). */
  storage: SecureStorage | null;
  baseUrl?: string;
  /** Renova este tanto antes de o access token vencer. */
  refreshMarginMs?: number;
  now?: () => number;
  /**
   * Coordenação entre abas (só web): todas as abas compartilham o MESMO cookie de refresh. Se duas
   * renovassem ao mesmo tempo, a segunda reapresentaria um token já trocado e a API encerraria a
   * sessão (proteção de reuso). Padrão: Web Locks + BroadcastChannel do navegador.
   */
  tabs?: { locks: LockManager | null; channel: BroadcastChannel | null };
};

/**
 * Sessão do usuário no app (web, desktop e celular).
 *  - Web: o refresh token fica em cookie httpOnly — o JavaScript nunca o vê.
 *  - Desktop/celular: o refresh token vem no corpo e vai para o armazenamento seguro do sistema.
 * A renovação é "voo único": chamadas simultâneas esperam a mesma renovação (renovar duas vezes com o
 * mesmo token seria tratado pela API como reuso e encerraria a sessão).
 */
export class AuthClient {
  private session: AuthSession | null = null;
  private inflight: Promise<AuthSession | null> | null = null;
  private listeners = new Set<Listener>();
  private readonly platformName: SessionPlatformName;
  private readonly margin: number;
  private readonly now: () => number;
  private readonly locks: LockManager | null;
  private readonly channel: BroadcastChannel | null;
  /** Muda a cada sessão recebida de outra aba (para não renovar de novo o que outra aba já renovou). */
  private peerVersion = 0;

  constructor(private readonly opts: AuthClientOptions) {
    if (opts.platform !== 'web' && !opts.storage) {
      throw new Error('Os apps precisam de armazenamento seguro para a sessão.');
    }
    this.platformName = PLATFORM_NAME[opts.platform];
    this.margin = opts.refreshMarginMs ?? 30_000;
    this.now = opts.now ?? Date.now;
    const tabs = opts.tabs ?? (opts.platform === 'web' ? browserTabs() : { locks: null, channel: null });
    this.locks = tabs.locks;
    this.channel = tabs.channel;
    this.channel?.addEventListener('message', (event: MessageEvent<PeerMessage>) => this.onPeer(event.data));
  }

  /** Mensagem de outra aba: sessão nova (login/renovação) ou saída. */
  private onPeer(message: PeerMessage) {
    if (message.type === 'session') {
      this.peerVersion += 1;
      this.set(message.session);
    } else if (message.type === 'logout') {
      this.set(null, 'logout');
    }
  }

  private broadcast(message: PeerMessage) {
    this.channel?.postMessage(message);
  }

  /** Encerra o canal entre abas (testes e troca de cliente). */
  dispose() {
    this.channel?.close();
    this.listeners.clear();
  }

  /** Plataforma do app (web, desktop, android). */
  get platform(): Platform {
    return this.opts.platform;
  }

  get current(): AuthSession | null {
    return this.session;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(session: AuthSession | null, change: SessionChange = 'update') {
    this.session = session;
    for (const l of this.listeners) l(session, change);
  }

  private async accept(res: AuthSessionResponse): Promise<AuthSession> {
    if (this.opts.storage) {
      if (!res.refreshToken) throw new Error('A API não devolveu a sessão do aplicativo.');
      await this.opts.storage.set(REFRESH_TOKEN_KEY, res.refreshToken);
    }
    const session = {
      accessToken: res.accessToken,
      expiresAt: this.now() + res.expiresIn * 1000,
      user: res.user,
    };
    this.set(session);
    this.broadcast({ type: 'session', session });
    return session;
  }

  private fresh(session: AuthSession | null): session is AuthSession {
    return !!session && session.expiresAt - this.margin > this.now();
  }

  async login(email: string, password: string): Promise<LoginOutcome> {
    const res = await apiRequest('/auth/login', {
      method: 'POST',
      body: { email, password, platform: this.platformName },
      schema: loginResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    return this.outcome(res);
  }

  /** Início do login pelo SSO da empresa (T-028d): devolve o endereço do provedor para onde o navegador vai. */
  async startSso(email: string, challenge: string, returnTo?: string): Promise<string> {
    const res = await apiRequest('/auth/sso/start', {
      method: 'POST',
      body: { email, platform: this.platformName, challenge, ...(returnTo ? { returnTo } : {}) },
      schema: ssoStartResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    return res.authorizationUrl;
  }

  /** Volta do SSO: troca o código de retorno pela sessão (ou pela segunda etapa do 2FA), com o verificador do início. */
  async completeSso(code: string, verifier: string): Promise<LoginOutcome> {
    const res = await apiRequest('/auth/sso/exchange', {
      method: 'POST',
      body: { code, verifier },
      schema: loginResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    return this.outcome(res);
  }

  private async outcome(res: z.infer<typeof loginResponseSchema>): Promise<LoginOutcome> {
    if ('challengeToken' in res) {
      return {
        kind: 'two-factor',
        step: res.twoFactor === 'setup' ? 'setup' : 'verify',
        challengeToken: res.challengeToken,
        expiresIn: res.expiresIn,
        ...(res.email ? { email: res.email } : {}),
      };
    }
    return { kind: 'session', session: await this.accept(res) };
  }

  /** Ativação obrigatória no login, passo 1: segredo para o QR code. */
  startLoginTwoFactorSetup(challengeToken: string): Promise<TwoFactorSetup> {
    return apiRequest('/auth/login/2fa/setup', {
      method: 'POST',
      body: { challengeToken },
      schema: twoFactorSetupSchema,
      baseUrl: this.opts.baseUrl,
    });
  }

  /**
   * Ativação obrigatória no login, passo 2: confirma o código. Devolve os códigos de recuperação e
   * `enter`, que abre a sessão — chamado só depois que a pessoa guardou os códigos.
   */
  async activateLoginTwoFactor(
    challengeToken: string,
    code: string,
  ): Promise<{ recoveryCodes: string[]; enter: () => Promise<AuthSession> }> {
    const res = await apiRequest('/auth/login/2fa/activate', {
      method: 'POST',
      body: { challengeToken, code },
      schema: twoFactorActivationResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    return { recoveryCodes: res.recoveryCodes, enter: () => this.accept(res) };
  }

  /**
   * Segunda etapa do login (o desafio carrega a plataforma do login). Devolve a sessão e, quando um
   * código de recuperação foi usado, quantos ainda restam.
   */
  async completeTwoFactor(
    challengeToken: string,
    proof: TwoFactorProof,
  ): Promise<{ session: AuthSession; recoveryCodesRemaining?: number }> {
    const res = await apiRequest('/auth/login/2fa', {
      method: 'POST',
      body: { challengeToken, ...proof },
      schema: twoFactorSessionResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    const session = await this.accept(res);
    return { session, recoveryCodesRemaining: res.recoveryCodesRemaining };
  }

  /** Renova a sessão. Devolve null quando não há sessão válida (é preciso entrar de novo). */
  refresh(): Promise<AuthSession | null> {
    this.inflight ??= this.refreshAcrossTabs().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /**
   * Uma aba por vez renova (lock exclusivo). Quem esperou o lock e, nesse meio-tempo, recebeu a
   * sessão renovada por outra aba usa essa sessão em vez de renovar de novo.
   */
  private async refreshAcrossTabs(): Promise<AuthSession | null> {
    if (!this.locks) return this.doRefresh();
    const versionAtStart = this.peerVersion;
    return this.locks.request(AUTH_LOCK_NAME, async () => {
      if (this.peerVersion !== versionAtStart && this.fresh(this.session)) return this.session;
      return this.doRefresh();
    });
  }

  private async doRefresh(): Promise<AuthSession | null> {
    let body: { refreshToken: string } | undefined;
    if (this.opts.storage) {
      const stored = await this.opts.storage.get(REFRESH_TOKEN_KEY);
      if (!stored) {
        this.set(null, 'ended');
        return null;
      }
      body = { refreshToken: stored };
    }
    try {
      const res = await apiRequest('/auth/refresh', {
        method: 'POST',
        body,
        schema: authSessionResponseSchema,
        baseUrl: this.opts.baseUrl,
      });
      return await this.accept(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        await this.opts.storage?.remove(REFRESH_TOKEN_KEY);
        this.set(null, 'ended');
        return null;
      }
      throw err;
    }
  }

  /** Token válido para chamar a API, renovando se estiver para vencer. */
  async getAccessToken(): Promise<string | null> {
    if (this.fresh(this.session)) return this.session.accessToken;
    const renewed = await this.refresh();
    return renewed?.accessToken ?? null;
  }

  /**
   * Encerra a sessão no servidor e no aparelho. Se o servidor não confirmar (sem rede, fora do ar),
   * NADA é limpo e o erro sobe: na web o cookie continuaria valendo e recarregar a página restauraria
   * a sessão — "sair" de mentira. Sessão que o servidor já não reconhece (401) conta como encerrada.
   */
  async logout(): Promise<void> {
    const stored = await this.opts.storage?.get(REFRESH_TOKEN_KEY);
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
        body: stored ? { refreshToken: stored } : undefined,
        headers: this.session ? { authorization: `Bearer ${this.session.accessToken}` } : undefined,
        baseUrl: this.opts.baseUrl,
      });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) throw err;
    }
    await this.opts.storage?.remove(REFRESH_TOKEN_KEY);
    this.set(null, 'logout');
    this.broadcast({ type: 'logout' });
  }

  /** Pede o link de redefinição de senha. A resposta é a mesma exista ou não a conta. */
  async requestPasswordReset(email: string): Promise<string> {
    const res = await apiRequest('/auth/forgot', {
      method: 'POST',
      body: { email },
      schema: forgotPasswordResponseSchema,
      baseUrl: this.opts.baseUrl,
    });
    return res.message;
  }

  /** O link de redefinição ainda vale? (false quando inválido, usado ou expirado). */
  async checkResetLink(token: string): Promise<boolean> {
    try {
      await apiRequest('/auth/reset/check', { method: 'POST', body: { token }, baseUrl: this.opts.baseUrl });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) return false;
      throw err;
    }
  }

  /**
   * Cria a nova senha pelo link. O servidor encerra TODAS as sessões da conta redefinida. Se este
   * aparelho estava logado, confere a sessão local: se era dessa conta, ela acaba aqui também; se
   * era de outra pessoa, continua.
   */
  async resetPassword(token: string, password: string): Promise<void> {
    await apiRequest('/auth/reset', {
      method: 'POST',
      body: { token, password },
      baseUrl: this.opts.baseUrl,
    });
    if (this.session) await this.refresh().catch(() => null);
  }

  /** Quem foi convidado e para qual empresa; null quando o convite é inválido, expirou ou já foi usado. */
  async checkInvitation(token: string): Promise<InvitationInfo | null> {
    try {
      return await apiRequest('/auth/invitation/check', {
        method: 'POST',
        body: { token },
        schema: invitationInfoSchema,
        baseUrl: this.opts.baseUrl,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.code === 'INVITATION_INVALID') return null;
      throw err;
    }
  }

  /** Aceita o convite: define a senha e ativa a conta (a pessoa entra em seguida pela tela de login). */
  async acceptInvitation(token: string, password: string): Promise<void> {
    await apiRequest('/auth/invitation/accept', {
      method: 'POST',
      body: { token, password },
      baseUrl: this.opts.baseUrl,
    });
  }

  /**
   * Chamada autenticada à API. Se o access token for recusado (expirou, sessão encerrada em outro
   * aparelho), renova uma vez e repete; se ainda assim for recusado, a sessão acabou: o app volta
   * para o login.
   */
  async request<S extends z.ZodType | undefined = undefined>(
    path: string,
    opts: RequestOptions<S> = {},
  ): Promise<S extends z.ZodType ? z.output<S> : unknown> {
    return this.authorized((token) =>
      apiRequest(path, {
        ...opts,
        baseUrl: opts.baseUrl ?? this.opts.baseUrl,
        headers: { ...opts.headers, authorization: `Bearer ${token}` },
      }),
    );
  }

  /** Download autenticado (ex.: PDF da ata), com a mesma renovação de sessão de `request`. */
  async download(path: string, opts: { signal?: AbortSignal } = {}): Promise<Blob> {
    return this.authorized((token) =>
      apiDownload(path, {
        signal: opts.signal,
        baseUrl: this.opts.baseUrl,
        headers: { authorization: `Bearer ${token}` },
      }),
    );
  }

  /** Exportação de relatório: o arquivo (baixado na hora) ou o pedido que foi para a fila (T-201d). */
  async fileOrQueued(
    path: string,
    opts: { query?: RequestOptions['query']; signal?: AbortSignal } = {},
  ): Promise<FileOrQueued> {
    return this.authorized((token) =>
      apiFileOrQueued(path, {
        query: opts.query,
        signal: opts.signal,
        baseUrl: this.opts.baseUrl,
        headers: { authorization: `Bearer ${token}` },
      }),
    );
  }

  private async authorized<T>(call: (token: string) => Promise<T>): Promise<T> {
    const token = await this.getAccessToken();
    if (!token) throw sessionEnded();
    try {
      return await call(token);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    }
    const renewed = await this.refresh();
    if (!renewed) throw sessionEnded();
    try {
      return await call(renewed.accessToken);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        this.set(null, 'ended');
        throw sessionEnded();
      }
      throw err;
    }
  }
}

function sessionEnded() {
  return new ApiError(401, 'SESSION_ENDED', 'Sua sessão terminou. Entre novamente.');
}

/** Coordenação nativa do navegador; ausente em ambientes sem esses recursos. */
function browserTabs(): { locks: LockManager | null; channel: BroadcastChannel | null } {
  const locks = typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : null;
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;
  return { locks, channel };
}
